import {ReactElement} from 'react'
import {graphql} from 'react-apollo'
import {get, flowRight as compose} from 'lodash'
import {Route, RouteChildrenProps, Switch, withRouter} from 'react-router-dom'
import {Button, Grid, Tabs, Tab} from '@material-ui/core'
import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import {Loading} from '../../Common/Loading'
import PageTitle from '../../Common/PageTitle'
import PageBody from '../../Common/PageBody'
import messages from '../../../assets/messages'
import {ACCOUNTS_QUERY, CLIENT_DATA_QUERY} from '../../../graphql/queries'
import {getAccountLabel} from '../../../common/utils/accounts'
import {Account, Locale} from '../../../graphql/types'
import {Viewer} from '../../../common/types'
import {Overview} from './scenes/Overview'
import {TransactionsHistory} from './scenes/TransactionsHistory'
import {useStyles} from './styles'

type TabItem = {
  path: string
  label: string
}

type BrokereePerformanceFeeAccountViewProps = {
  account: Account
  viewer: Viewer & {locale: Locale}
  accountLoading: boolean
  viewerLoading: boolean
} & WithNamespaces & RouteChildrenProps

function BrokereePerformanceFeeAccountView(props: BrokereePerformanceFeeAccountViewProps): ReactElement {
  const {account, viewer, accountLoading, viewerLoading, t, history, match, location} = props
  const {locale} = viewer

  const classes = useStyles()

  const tabs: TabItem[] = [
    {path: '', label: t(messages.overview.i18nKey, messages.overview.defaults)},
    {path: '/transactions', label: t(messages.transactionsHistory.i18nKey, messages.transactionsHistory.defaults)},
  ]

  const currentTabIndex = Math.max(tabs.findIndex(tab => location.pathname === `${match?.url}${tab.path}`), 0)

  const handleCloseDialog = () => {
     history.push('/accounts')
  }

  const handleWithdrawClick = () => {
    history.push(`/transactions/${account.id}/withdraw`)
  }

  const handleTransferClick = () => {
    history.push(`/transactions/${account.id}/transfer/transferFunds`)
  }

  const handleChangeTab = (_: unknown, tabIndex: number) => {
    const currentTab = tabs[tabIndex]
    history.push(`${match?.url}${currentTab.path}`)
  }

  const handleTabClick = (tab: TabItem) => {
    history.push(`/accounts/${account.id}${tab.path}`)
  }

  if (accountLoading || viewerLoading || !account) return <Loading />

  return (
    <>
      <PageTitle
        title={getAccountLabel(account, locale)}
        onBack={handleCloseDialog}
        rightAction={
          <Grid className={classes.controls}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              onClick={handleWithdrawClick}
            >
              <Trans {...messages.withdraw} />
            </Button>
            <Button
              variant="contained"
              size="large"
              color="primary"
              onClick={handleTransferClick}
            >
              <Trans {...messages.transfer} />
            </Button>
          </Grid>
        }
      >
        <Tabs
          variant="standard"
          value={currentTabIndex}
          onChange={handleChangeTab}
        >
          {tabs.map((tab) =>
            <Tab
              key={tab.path}
              label={tab.label}
              textColor="inherit"
              onClick={() => handleTabClick(tab)}
            />
          )}
        </Tabs>
      </PageTitle>
      <PageBody>
        <Switch>
          <Route exact path={match?.path} render={() => <Overview account={account}/>} />
          <Route path={`${match?.path}/transactions`} render={() => <TransactionsHistory account={account} />} />
        </Switch>
      </PageBody>
    </>
  )
}

export default compose(
  withNamespaces(),
  withRouter,
  graphql(ACCOUNTS_QUERY, {
    props: ({data: {loading: accountLoading}, data, ownProps: {match}}: any) => {
      const {accountId} = match.params
      const accounts = get(data, 'viewer.accounts', [])
      const account = accounts.find(account => account.id === Number(accountId))
      return {
        accountLoading,
        account,
      }
    }
  }),
  graphql(CLIENT_DATA_QUERY, {
    props: ({data: {loading: viewerLoading}, data}: any) => ({
      viewerLoading,
      viewer: get(data, 'viewer'),
    })
  })
)(BrokereePerformanceFeeAccountView)
